import { ReactElement, useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { Address } from 'viem';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { ONE_MINUTE_MS } from 'modules/common/const';
import { globalTranslation, useTranslation } from 'modules/i18n';
import { IPoolUnstake } from 'modules/pool/types';
import { getClaimTimeLeft } from 'modules/pool/utils/getClaimTimeLeft';

interface IClaimTabStatusProps {
  poolAddress: Address;
  poolUnstakes: IPoolUnstake[];
}

const CHIP_MIN_WIDTH = 24;

export function ClaimTabStatus({
  poolAddress,
  poolUnstakes,
}: IClaimTabStatusProps): ReactElement | null {
  const { t, keys } = useTranslation(globalTranslation);
  const [claimableUnstakes, setClaimableUnstakes] = useState(0);
  const totalUnstakes = poolUnstakes.length;

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery({
    poolAddress,
  });

  useEffect(() => {
    const updateTimer = () => {
      if (!unstakePeriodDays) {
        return;
      }

      setClaimableUnstakes(
        poolUnstakes?.filter(
          ({ executedAt }) => !getClaimTimeLeft(executedAt, unstakePeriodDays),
        ).length ?? 0,
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, ONE_MINUTE_MS);

    return () => clearInterval(interval);
  }, [poolUnstakes, unstakePeriodDays]);

  if (claimableUnstakes === 0 && totalUnstakes === 0) {
    return null;
  }

  if (claimableUnstakes === 0 && totalUnstakes !== 0) {
    return (
      <Chip
        color="warning"
        label={totalUnstakes}
        size="small"
        sx={{ minWidth: CHIP_MIN_WIDTH }}
      />
    );
  }

  if (claimableUnstakes > 0 && claimableUnstakes <= totalUnstakes) {
    return (
      <Chip
        color="success"
        label={
          claimableUnstakes === totalUnstakes
            ? totalUnstakes
            : `${claimableUnstakes} ${t(keys.common.of)} ${totalUnstakes}`
        }
        size="small"
        sx={{ minWidth: CHIP_MIN_WIDTH }}
      />
    );
  }

  return null;
}
